import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { StorefrontProduct } from '@/types'

export interface CategoryCount {
  name: string
  count: number
}

/**
 * The public catalog. Home and the products page share this one query key, so
 * react-query serves whichever loads second straight from cache - keep the key
 * here rather than re-declaring it per page.
 */
export function useStorefrontProducts() {
  return useQuery({
    queryKey: ['storefront-products'],
    queryFn: async () => (await api.get<{ data: StorefrontProduct[] }>('/storefront/products')).data.data,
  })
}

/**
 * Categories are not a backend concept - `products.category` is a free-text
 * column with no table, endpoint or ordering behind it. So the list and its
 * counts are derived from the catalog that is already on the wire. Sorted
 * alphabetically because the products arrive ordered by product name, which
 * would otherwise put the sidebar in an arbitrary order.
 */
export function categoriesOf(products: StorefrontProduct[] | undefined): CategoryCount[] {
  const counts = new Map<string, number>()

  for (const product of products ?? []) {
    counts.set(product.category, (counts.get(product.category) ?? 0) + 1)
  }

  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name))
}
